import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#selectInitialSlide();
  }

  /**
   * Opens the gallery on the selected variant's image, for ?variant= deep links. The gallery
   * order no longer depends on the variant, so the server passes the slide index instead.
   *
   * Re-asserted for half a second on purpose. The slideshow builds its scroller
   * asynchronously, so an early select() silently does nothing; and its own setup then runs
   * select(0), which would scroll back off our slide if we stopped at the first success.
   * Stops early only if the shopper touches the gallery.
   */
  #selectInitialSlide() {
    const index = Number(this.dataset.selectedIndex ?? 0);

    if (!index) return;

    const { signal } = this.#controller;
    let cancelled = false;
    let frames = 0;

    const cancel = () => (cancelled = true);
    this.addEventListener('pointerdown', cancel, { once: true, signal });
    this.addEventListener('touchstart', cancel, { once: true, passive: true, signal });

    const attempt = () => {
      if (cancelled || !this.isConnected) return;

      const scroller = this.querySelector('slideshow-slides');
      const slide = this.querySelectorAll('slideshow-slide')[index];
      const atTarget =
        scroller && slide && Math.abs(slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left) < 2;

      if (!atTarget) this.slideshow?.select(index, undefined, { animate: false });

      if ((frames += 1) < 30) requestAnimationFrame(attempt);
    };

    requestAnimationFrame(attempt);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // The gallery order is stable across variants, so the markup that comes back from the
    // variant fetch is normally identical to what is already on screen. Replacing it anyway
    // would throw away decoded images and reset the carousel to the first slide, undoing the
    // instant jump done on variant:selected roughly a quarter of a second earlier.
    if (this.#mediaSignature(newMediaGallery) === this.#mediaSignature(this)) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Identifies the set and order of images in a gallery, so two galleries can be compared.
   *
   * @param {Element} gallery - The gallery to fingerprint.
   * @returns {string}
   */
  #mediaSignature(gallery) {
    return Array.from(gallery.querySelectorAll('img'))
      .map((image) => (image.getAttribute('src') ?? '').split('?')[0])
      .join('|');
  }

  /** @type {Record<string, number> | null} */
  #optionMedia = null;

  /**
   * Moves the gallery to the selected option's image immediately, without waiting for the
   * variant fetch to come back. Every colour's image is already in the DOM, so this is a
   * carousel index change and nothing more.
   *
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id;

    if (!optionValueId) return;

    if (!this.#optionMedia) {
      try {
        this.#optionMedia = JSON.parse(this.dataset.optionMedia || '{}');
      } catch {
        this.#optionMedia = {};
      }
    }

    const index = this.#optionMedia[optionValueId];

    if (typeof index !== 'number') return;

    this.slideshow?.select(index, undefined, { animate: false });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
